/**
 * One queue for everything that opens or closes the microphone.
 *
 * The live call (`use-assistant-live.ts`, over `live-audio.ts`) and dictation
 * (`use-dictation.ts`) each take the audio session and give it back, and on
 * iOS giving it back — `setAudioSessionActivity(false)` — stops whatever is
 * recording under it, whoever started it. Each hook already queued its own
 * work so a stop and a start never raced; two queues side by side still
 * raced each other: Type instead ends the call, the person taps the
 * composer's mic, dictation opens the session, and the call's teardown,
 * still running, closes it under the new recording, which then hears
 * nothing. One queue for both means a start always runs after every stop
 * asked for before it has finished. Android has no session to lose, and
 * waits the same few milliseconds.
 *
 * A job that fails does not stop the queue: the next one runs regardless,
 * and the failure is the caller's, on the promise it was handed.
 */
let tail: Promise<void> = Promise.resolve();

export const onMicQueue = <T>(job: () => Promise<T>): Promise<T> => {
  const run = tail.then(job);
  tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
};
